import { ComponentFactoryResolver, ComponentRef, Injectable, Injector, Type } from '@angular/core';
import { EnergyTypeMarker, EPMarker } from '@app/shared/models/energiekarte/epmarker';
import { isNil } from 'lodash';
import { NewLegendsService } from '../components-new/legends/new-legends.service';
import { MapStoreService } from '../services/map-store.service';
import { EnergyTypeIconComponent } from '../components/energy-type-icon/energy-type-icon.component';
import * as L from 'leaflet';
import { ProducerEnergyTypeCodes, ProducerEnergyTypes } from '@app/shared/enums';
import { Subject, takeUntil } from 'rxjs';
import { MapPopupComponent } from '../map-popup/map-popup.component';
import { ProducerInfoPopupComponent } from '../map-popup/producer-info-popup/producer-info-popup.component';
import { EnergyTypeProducers } from '@app/shared/models/energiekarte/energy-type-producers';

@Injectable({
  providedIn: 'root',
})
export class ProducerLayerService {
  private unsubscribe$ = new Subject<void>();

  constructor(
    private legendsService: NewLegendsService,
    private mapStoreService: MapStoreService,
    public resolver: ComponentFactoryResolver,
    public injector: Injector
  ) {}

  public initializeProducers(producers: EnergyTypeMarker[]): void {
    const availableAssetTypes = this.getAvailableAssetTypes(producers);
    const clusters = this.createClusters(availableAssetTypes);
    this.updateLegendVisibility(availableAssetTypes);
    this.updateClusters(clusters);
    this.legendsService.updateAvailableProducers(Array.from(availableAssetTypes));
    producers.forEach((assetTypeMarker) => this.addMarkers(assetTypeMarker));
    this.updateProducerLayers();
  }

  private getAvailableAssetTypes(producers: EnergyTypeMarker[]): Set<string> {
    return new Set(
      producers.map((p) => {
        switch (p.assetType.code) {
          case ProducerEnergyTypeCodes.PHOTOVOLTAICS:
            return 'Photovoltaik';
          case ProducerEnergyTypeCodes.BIOMASS:
            return 'Biomasse';
          case ProducerEnergyTypeCodes.WATER:
            return 'Wasser';
          case ProducerEnergyTypeCodes.WIND:
            return 'Wind';
          default:
            return '';
        }
      })
    );
  }

  private createClusters(availableAssetTypes: Set<string>): Record<string, L.MarkerClusterGroup> {
    const clusters: Record<string, L.MarkerClusterGroup> = {};

    Object.values(ProducerEnergyTypes).forEach((type) => {
      if (availableAssetTypes.has(type)) {
        clusters[type] = L.markerClusterGroup({
          spiderfyOnMaxZoom: false,
          spiderfyDistanceMultiplier: 4,
          iconCreateFunction: this.createProducerClusterMarkerIcon.bind(this),
        });
        this.addClusterEvent(clusters[type]);
      }
    });

    return clusters;
  }

  private addClusterEvent(clusters: any): void {
    clusters.on('clusterclick', (event: L.LeafletMouseEvent) => {
      const clusterMarker = event.propagatedFrom || event.target;
      const mapCluster = clusterMarker._map;
      const currentZoom = mapCluster.getZoom();
      const maxZoom = mapCluster.getMaxZoom();

      if (currentZoom === maxZoom) {
        clusterMarker.spiderfy();
      }
    });
  }

  private updateLegendVisibility(availableAssetTypes: Set<string>): void {
    availableAssetTypes.forEach((type) => {
      const isVisible = (this.legendsService as any)[`is${type}Visible`] || false;
      (this.legendsService as any)[`_is${type}Visible$`].next(isVisible);
    });

    const anyProducerVisible = Array.from(availableAssetTypes).some((type) => (this.legendsService as any)[`is${type}Visible`]);
    this.legendsService._isProducersVisible$.next(anyProducerVisible);
  }

  private updateClusters(clusters: Record<string, L.MarkerClusterGroup>): void {
    const clusterMap = {
      [ProducerEnergyTypes.PHOTOVOLTAICS]: 'Photovoltaik',
      [ProducerEnergyTypes.BIOMASS]: 'Biomasse',
      [ProducerEnergyTypes.WATER]: 'Wasser',
      [ProducerEnergyTypes.WIND]: 'Wind',
    };

    Object.entries(clusterMap).forEach(([type, clusterName]) => {
      if (clusters[type]) {
        (this.mapStoreService as any)[`update${clusterName}Cluster`](clusters[type]);
      }
    });
  }

  public clearMarkers(): void {}

  private addMarkers(assetTypeMarker: EnergyTypeMarker): void {
    assetTypeMarker.icon = assetTypeMarker.assetType.name.toLowerCase();

    const assetTypeIconComponent = this.createEnergyTypeIconComponent();
    assetTypeIconComponent.instance.iconName = assetTypeMarker.icon;

    assetTypeIconComponent.changeDetectorRef.detectChanges();

    const icon = new L.DivIcon({
      html: assetTypeIconComponent.location.nativeElement,
      iconSize: this.mapStoreService.defaultEnergyTypeIconSize,
      iconAnchor: [30, 30],
      popupAnchor: [70, -10],
    });

    const latitude = isNil(assetTypeMarker.latitude)
      ? 0
      : this.hasSimilarCoordinates(assetTypeMarker)
      ? assetTypeMarker.latitude + 0.2 * 0.00001
      : assetTypeMarker.latitude;

    const longitude = isNil(assetTypeMarker.longitude)
      ? 0
      : this.hasSimilarCoordinates(assetTypeMarker)
      ? assetTypeMarker.longitude + 0.5 * 0.0001
      : assetTypeMarker.longitude;

    const marker = new EPMarker([latitude, longitude], {
      icon: icon,
      id: assetTypeMarker.producerGuid,
      data: assetTypeMarker,
      type: 'mapmarker',
    });

    marker.bindPopup(L.popup({ closeButton: false }).setContent(''));

    marker.on('popupopen', () => {
      this.initializeProducerPopup(marker);

      assetTypeIconComponent.instance.isIconActive = true;
      assetTypeIconComponent.changeDetectorRef.detectChanges();
    });

    marker.on('popupclose', function () {
      assetTypeIconComponent.instance.isIconActive = false;
      assetTypeIconComponent.changeDetectorRef.detectChanges();
    });

    assetTypeMarker.marker = marker;
    assetTypeMarker.component = assetTypeIconComponent;

    switch (assetTypeMarker.assetType.code) {
      case ProducerEnergyTypeCodes.PHOTOVOLTAICS:
        marker.addTo(this.mapStoreService.photovoltaicCluster);
        break;
      case ProducerEnergyTypeCodes.BIOMASS:
        marker.addTo(this.mapStoreService.biomasseCluster);
        break;
      case ProducerEnergyTypeCodes.WATER:
        marker.addTo(this.mapStoreService.waterCluster);
        break;
      case ProducerEnergyTypeCodes.WIND:
        marker.addTo(this.mapStoreService.windCluster);
        break;
    }

    this.mapStoreService.addProducerMarker(marker);
  }

  private hasSimilarCoordinates(marker: EnergyTypeMarker): boolean {
    return this.mapStoreService.producerMarkers.some(
      (existingMarker: any) => existingMarker.options.data.latitude === marker.latitude && existingMarker.options.data.longitude === marker.longitude
    );
  }

  public createProducerClusterMarkerIcon(markers: any): L.DivIcon {
    const childMarkers = markers.getAllChildMarkers();
    const photovoltaics = childMarkers.filter(
      (producersMarkers: any) => producersMarkers.options.data.assetType.code === ProducerEnergyTypeCodes.PHOTOVOLTAICS
    );
    const biomass = childMarkers.filter((producersMarkers: any) => producersMarkers.options.data.assetType.code === ProducerEnergyTypeCodes.BIOMASS);
    const water = childMarkers.filter((producersMarkers: any) => producersMarkers.options.data.assetType.code === ProducerEnergyTypeCodes.WATER);
    const wind = childMarkers.filter((producersMarkers: any) => producersMarkers.options.data.assetType.code === ProducerEnergyTypeCodes.WIND);

    let divContainer = `
    <div class='producer-container'>
        <div class='${photovoltaics.length > 0 ? 'producer-icon' : 'display-none'}'><img alt="marker-img" src="${
      photovoltaics ? '/assets/icons/producers/photovoltaik.png' : ''
    }"/><div class='producer-icon-badge'>${photovoltaics.length}</div></div>

        <div class='${biomass.length > 0 ? 'producer-icon' : 'display-none'}'><img alt="marker-img" src="${
      biomass ? '/assets/icons/producers/biomass.png' : ''
    }"/><div class='producer-icon-badge'>${biomass.length}</div></div>

        <div class='${water.length > 0 ? 'producer-icon' : 'display-none'}'><img alt="marker-img" src="${
      water ? '/assets/icons/producers/wasser.png' : ''
    }"/><div class='producer-icon-badge'>${water.length}</div></div>

        <div class='${wind.length > 0 ? 'producer-icon' : 'display-none'}'><img alt="marker-img" src="${
      wind ? '/assets/icons/producers/wind.png' : ''
    }"/><div class='producer-icon-badge'>${wind.length}</div></div>
    </div>
        `;

    return L.divIcon({
      className: 'custom-div-icon',
      html: divContainer,
      iconSize: [48, 48],
      iconAnchor: [30, 30],
    });
  }

  private createEnergyTypeIconComponent(): ComponentRef<EnergyTypeIconComponent> {
    return this.resolver.resolveComponentFactory(EnergyTypeIconComponent).create(this.injector);
  }

  private updateProducerLayers(): void {
    this.toggleLayer(this.legendsService.isProducersVisible, this.mapStoreService.producerCluster);
    this.toggleLayer(this.legendsService.isPhotovoltaikVisible, this.mapStoreService.photovoltaicCluster);
    this.toggleLayer(this.legendsService.isBiomasseVisible, this.mapStoreService.biomasseCluster);
    this.toggleLayer(this.legendsService.isWasserVisible, this.mapStoreService.waterCluster);
    this.toggleLayer(this.legendsService.isWindVisible, this.mapStoreService.windCluster);
  }

  private toggleLayer(isVisible: boolean, cluster: L.MarkerClusterGroup | null): void {
    if (!cluster) return;

    if (isVisible) {
      (this.mapStoreService.map as L.Map).addLayer(cluster);
    } else {
      (this.mapStoreService.map as L.Map).removeLayer(cluster);
    }
  }

  private initializeProducerPopup(marker: any): void {
    const component = this.initializeProducerInfoPopup(ProducerInfoPopupComponent, marker.options.data);

    marker.options.component = component;

    const popup = marker.getPopup();
    popup.setContent(component.location.nativeElement);

    return marker;
  }

  private initializeProducerInfoPopup(popupComponent: Type<MapPopupComponent>, producerInfo: EnergyTypeProducers): ComponentRef<MapPopupComponent> {
    const component = this.resolver.resolveComponentFactory(popupComponent).create(this.injector);

    component.instance.data = producerInfo;
    component.instance.closeEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.closePopup());
    component.changeDetectorRef.detectChanges();

    return component;
  }

  closePopup = () => {
    (this.mapStoreService.map as L.Map).closePopup();
  };
}
